import { useEffect, useRef } from "react"
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "motion/react"

interface TttLogoProps {
  frontSrc: string
  backSrc: string
}

const TILT_SPRING = { stiffness: 150, damping: 18, mass: 0.6 }
const GLARE_SPRING = { stiffness: 250, damping: 30 }
const MAX_TILT = 12 // degrees

// React port of Ttt::Logo::Component, upgraded from the raw CSS-var tilt to a
// spring-smoothed 3D parallax: the logo glides toward the cursor with inertia,
// a light reflection sweeps across the logo (masked to its shape) following the
// pointer, and it scales up subtly as the cursor gets close. Falls back to a
// static logo when the user prefers reduced motion or is on a touch device.
export default function Logo({ frontSrc, backSrc }: TttLogoProps) {
  const ref = useRef<HTMLDivElement>(null)
  const prefersReducedMotion = useReducedMotion()

  // Raw pointer-driven targets; springs below smooth them frame to frame.
  const tiltXTarget = useMotionValue(0)
  const tiltYTarget = useMotionValue(0)
  const scaleTarget = useMotionValue(1)
  const glareXTarget = useMotionValue(50)
  const glareYTarget = useMotionValue(50)
  const glareOpacity = useMotionValue(0)

  const rotateX = useSpring(tiltXTarget, TILT_SPRING)
  const rotateY = useSpring(tiltYTarget, TILT_SPRING)
  const scale = useSpring(scaleTarget, { stiffness: 200, damping: 20 })
  const glareX = useSpring(glareXTarget, GLARE_SPRING)
  const glareY = useSpring(glareYTarget, GLARE_SPRING)
  const smoothGlareOpacity = useSpring(glareOpacity, GLARE_SPRING)

  const glareBackground = useMotionTemplate`radial-gradient(circle at ${glareX}% ${glareY}%, rgba(255,255,255,0.55), rgba(255,255,255,0) 55%)`

  useEffect(() => {
    if (prefersReducedMotion) return
    const isTouchDevice =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as unknown as { msMaxTouchPoints: number }).msMaxTouchPoints > 0
    if (isTouchDevice) return

    const handleMouseMove = (event: MouseEvent) => {
      const el = ref.current
      if (!el) return

      const middleX = window.innerWidth / 2
      const middleY = window.innerHeight / 2
      tiltXTarget.set((-(event.clientY - middleY) / middleY) * MAX_TILT)
      tiltYTarget.set(((event.clientX - middleX) / middleX) * MAX_TILT)

      const rect = el.getBoundingClientRect()
      const localX = ((event.clientX - rect.left) / rect.width) * 100
      const localY = ((event.clientY - rect.top) / rect.height) * 100
      glareXTarget.set(Math.max(-20, Math.min(120, localX)))
      glareYTarget.set(Math.max(-20, Math.min(120, localY)))

      // Proximity: 1 when the cursor sits on the logo, fading out ~1 logo-width away.
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const distance = Math.hypot(event.clientX - centerX, event.clientY - centerY)
      const proximity = Math.max(0, 1 - distance / rect.width)
      scaleTarget.set(1 + proximity * 0.05)
      glareOpacity.set(0.12 + proximity * 0.45)
    }

    const reset = () => {
      tiltXTarget.set(0)
      tiltYTarget.set(0)
      scaleTarget.set(1)
      glareOpacity.set(0)
    }

    window.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseleave", reset)
    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseleave", reset)
    }
  }, [
    prefersReducedMotion,
    tiltXTarget,
    tiltYTarget,
    scaleTarget,
    glareXTarget,
    glareYTarget,
    glareOpacity,
  ])

  const maskStyle = {
    WebkitMaskImage: `url(${frontSrc})`,
    maskImage: `url(${frontSrc})`,
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  } as const

  if (prefersReducedMotion) {
    return (
      <div className="w-full ttt-logo-3d">
        <div className="ttt-logo-layer-back">
          <img src={backSrc} alt="TTT Logo Background" className="w-full h-full" />
        </div>
        <div className="ttt-logo-layer-front">
          <img src={frontSrc} alt="TTT" className="w-full h-full" />
        </div>
      </div>
    )
  }

  return (
    <motion.div
      ref={ref}
      className="relative w-full max-w-[300px] md:max-w-[450px] lg:max-w-[600px]"
      style={{
        aspectRatio: "1 / 1",
        transformStyle: "preserve-3d",
        transformPerspective: 1000,
        rotateX,
        rotateY,
        scale,
      }}
    >
      <div className="ttt-logo-layer-back">
        <img src={backSrc} alt="TTT Logo Background" className="w-full h-full" />
      </div>
      <div className="ttt-logo-layer-front">
        <img src={frontSrc} alt="TTT" className="w-full h-full" />
      </div>

      {/* Cursor-following sheen, clipped to the logo silhouette. */}
      <div
        className="absolute inset-0"
        style={{ transform: "translateZ(45px)", pointerEvents: "none" }}
      >
        <motion.div
          data-testid="logo-glare"
          className="absolute inset-0"
          style={{
            background: glareBackground,
            opacity: smoothGlareOpacity,
            mixBlendMode: "screen",
            ...maskStyle,
          }}
        />
      </div>
    </motion.div>
  )
}
